import { FunnelType, SessionType, StripeAccountType } from '../_models';

export enum DynamicChildrenKey {
  Reports = 'reportsUserConfig',
  LegacyReports = 'legacyReportsUserConfig',
  LegacyGlobalReports = 'legacyReportsGlobalConfig',
}

export interface NavContext {
  hasUser: boolean;
  isStaff: boolean;
  hasAccount: boolean;
  accountId: number | string;
  profilerEnabled: boolean;
  cprofilerEnabled: boolean;
  mouseflowAvailable: boolean;
  mouseflowEnabled: boolean;
}

export enum MenuId {
  Home = 'home',
  Retention = 'retention',
  Returns = 'returns',
  TrustIq = 'trustiq',
  Resolve = 'resolve',
  Support = 'support',
  Engage = 'engage',
}

export enum NavItemId {
  TrustIqMerchantProfiles = 'trustiq-merchant-profiles',
  ResolveMerchantProfiles = 'resolve-merchant-profiles',
}

export interface NavItem {
  id: string;
  label: string;
  opensMenu?: MenuId;
  opensLegacy?: boolean;
  adminOnlyBadge?: boolean;
  newBadge?: boolean;
  icon?: string;
  route?: any[];
  queryParams?: { [key: string]: any };
  routeParams?: { [key: string]: any };
  exact?: boolean;
  title?: string;
  children?: NavItem[];
  adminOnly?: boolean;
  requiresAccount?: boolean;
  requiresUser?: boolean;
  visible?: boolean;
  comingSoon?: boolean;
  homeOnly?: boolean;
  locked?: boolean;
  showWhen?: (ctx: NavContext) => boolean;
  dynamicLabel?: (ctx: NavContext) => string;
  dynamicRoute?: (ctx: NavContext) => any[];
  dynamicChildrenKey?: DynamicChildrenKey;
}

export const SupportPortalParams = {
  resourcetype: FunnelType.Visual,
  session_type: SessionType.SupportPortal,
};

export const RetentionParams = {
  resourcetype: FunnelType.Visual,
  session_type: SessionType.Retention,
};

export const RetentionDemoParams = {
  resourcetype: FunnelType.Visual,
  session_type: SessionType.RetentionDemo,
};

export const MarketplaceParams = {
  resourcetype: FunnelType.Visual,
  session_type: SessionType.Marketplace,
};

export const PaymentUpdaterParams = {
  resourcetype: FunnelType.Visual,
  session_type: SessionType.PaymentUpdate,
};

export const GatewayPaymentSourcesParams = {
  stripe_account_type: StripeAccountType.Gateway,
};

export interface ProductMenu {
  id: MenuId;
  label?: string;
  icon?: string;
  items: NavItem[];
}

export const ButtonItems: NavItem[] = [
  {
    id: 'apps-integrations',
    label: 'Apps & Integrations',
    icon: 'assets/MenuIcons/apps-integrations.svg',
    homeOnly: true,
    comingSoon: true,
  },
  {
    id: 'account-settings',
    label: 'Account Settings',
    icon: 'assets/MenuIcons/account-settings.svg',
    requiresUser: true,
    children: [
      { id: 'personal-info', label: 'Personal Info', route: ['/personal-info'] },
      { id: 'password-change', label: 'Password Change', route: ['/password-change'] },
      { id: 'invoices', label: 'Invoices', route: ['/invoices'] },
    ],
  },
  {
    id: 'admin-controls',
    label: 'Admin Controls',
    adminOnlyBadge: true,
    icon: 'assets/MenuIcons/admin-controls.svg',
    requiresUser: true,
    children: [
      { id: 'select-account', adminOnly: true, label: 'Select Account', route: ['/accounts'] },
      { id: 'billing-plans', adminOnly: true, label: 'Billing Plans', route: ['/plans'] },
      { id: 'resolve-plans', adminOnly: true, label: 'Resolve Plans', route: ['/resolve-plans'] },
      { id: 'users', label: 'Users', route: ['/users'], requiresAccount: true },
      { id: 'admin-offer-builder', adminOnly: true, label: 'Offer Builder', route: ['/admin', 'retention'] },
      { id: 'admin-single-steps', adminOnly: true, label: 'Single Steps', route: ['/single-step'], requiresAccount: true },
      { id: 'admin-survey', adminOnly: true, label: 'Survey', route: ['/admin', 'survey'] },
      { id: 'blacklist', adminOnly: true, label: 'Blacklist', dynamicRoute: (ctx: NavContext) => ['/email-blacklist', ctx.accountId] },
      { id: 'api-keys', adminOnly: true, label: 'API Keys', route: ['/api-keys'] },
      { id: 'ticket-system', adminOnly: true, label: 'Ticket System', route: ['/ticket-system'] },
      { id: 'shipping-system', adminOnly: true, label: 'Shipping System', route: ['/shipping-system'] },
      { id: 'dev-env', adminOnly: true, label: 'Development Environments', route: ['/dev-env'] },
      {
        id: 'profiler-enable',
        label: 'Enable Silky Profiler',
        adminOnly: true,
        route: ['/profiler/enable'],
        showWhen: (ctx: NavContext) => !ctx.profilerEnabled,
      },
      {
        id: 'profiler-disable',
        label: 'Disable Silky Profiler',
        adminOnly: true,
        route: ['/profiler/disable'],
        showWhen: (ctx: NavContext) => ctx.profilerEnabled,
      },
      {
        id: 'cprofiler-enable',
        label: 'Enable CProfiler',
        adminOnly: true,
        route: ['/profiler/enable'],
        routeParams: { type: 'cprofiler' },
        showWhen: (ctx: NavContext) => !ctx.cprofilerEnabled,
      },
      {
        id: 'cprofiler-disable',
        label: 'Disable CProfiler',
        adminOnly: true,
        route: ['/profiler/disable'],
        routeParams: { type: 'cprofiler' },
        showWhen: (ctx: NavContext) => ctx.cprofilerEnabled,
      },
      {
        id: 'mouseflow-toggle',
        label: 'Mouseflow Recordings',
        adminOnly: true,
        route: ['/mouseflow/toggle'],
        showWhen: (ctx: NavContext) => ctx.mouseflowAvailable,
        dynamicLabel: (ctx: NavContext) => {
          let prefix = 'Enable';
          if (ctx.mouseflowEnabled) {
            prefix = 'Disable';
          }
          return prefix + ' Mouseflow Recordings';
        },
      },
      {
        id: 'admin-checkouts',
        label: 'Checkouts',
        adminOnly: true,
        requiresAccount: true,
        children: [
          { id: 'checkouts-list', label: 'Checkouts', route: ['/checkouts'], exact: true },
          { id: 'bank-profiles', label: 'Bank Profiles', route: ['/checkouts', 'bank-profiles'] },
          { id: 'processor-profiles', label: 'Processor Profiles', route: ['/checkouts', 'processor-profiles'] },
          {
            id: 'payment-sources',
            label: 'Payment Sources',
            route: ['/payment', 'sources'],
            queryParams: GatewayPaymentSourcesParams,
          },
        ],
      },
      {
        id: 'admin-logs',
        label: 'Logs',
        adminOnly: true,
        children: [
          { id: 'phone-logs', label: 'Phone Calls', route: ['/phone-logs'] },
          { id: 'sms-logs', label: 'SMS Messages', route: ['/sms-logs'] },
          { id: 'email-logs', label: 'Emails', route: ['/email-logs'] },
        ],
      },
    ],
  },
  {
    id: 'legacy',
    label: 'Legacy',
    icon: 'assets/MenuIcons/legacy.svg',
    // Anyone may open the old sidebar; the rows inside keep the gates they always had.
    opensLegacy: true,
    requiresAccount: true,
  },
];

export const HomeMenu: ProductMenu = {
  id: MenuId.Home,
  items: [
    { id: 'home', label: 'Home', icon: 'assets/MenuIcons/home.svg', route: ['/'], exact: true, requiresAccount: true },
    { id: 'visitors', label: 'Visitors', icon: 'assets/MenuIcons/visitors.svg', route: ['/visitors'], requiresAccount: true },
    {
      id: 'portals',
      label: 'Portals',
      icon: 'assets/MenuIcons/portals.svg',
      requiresAccount: true,
      children: [
        { id: 'portals-customer', label: 'Customer Portal', route: ['/order-management-portal'], requiresAccount: true },
        { id: 'portals-support', label: 'Support Portal', comingSoon: true },
        { id: 'portals-returns', label: 'Returns Portal', comingSoon: true },
        { id: 'portals-track-order', label: 'Track Order Portal', comingSoon: true },
      ],
    },
    {
      id: 'brand-settings',
      label: 'Brand Settings',
      icon: 'assets/MenuIcons/brand-settings.svg',
      route: ['/campaign/general'],
      requiresAccount: true,
    },
  ],
};

// Locks are static: nothing in the backend answers "does this account have this" yet.
export const ProductEntries: NavItem[] = [
  { id: 'product-retention', label: 'Retention', icon: 'assets/MenuIcons/retention.svg', opensMenu: MenuId.Retention },
  {
    id: 'product-returns',
    label: 'Returns',
    icon: 'assets/MenuIcons/returns.svg',
    opensMenu: MenuId.Returns,
    requiresAccount: true,
    adminOnly: true,
  },
  // Customer Support: the design shows the product but never specifies its menu.
  { id: 'product-support', label: 'Customer Support', icon: 'assets/MenuIcons/customer-support.svg', comingSoon: true },
  { id: 'product-resolve', label: 'Resolve', icon: 'assets/MenuIcons/resolve.svg', opensMenu: MenuId.Resolve, newBadge: true },
  {
    id: 'product-trustiq',
    label: 'TrustIQ',
    icon: 'assets/MenuIcons/trustiq.svg',
    opensMenu: MenuId.TrustIq,
    newBadge: true,
    requiresAccount: true,
    adminOnly: true,
  },
  // Engage does not exist in the frontend or the backend.
  { id: 'product-engage', label: 'Engage', icon: 'assets/MenuIcons/engage.svg', locked: true, comingSoon: true },
];

export const ProductMenus: ProductMenu[] = [
  {
    id: MenuId.Retention,
    label: 'Retention',
    icon: 'assets/MenuIcons/retention-product.svg',
    items: [
      { id: 'retention-dashboard', label: 'Dashboard', icon: 'assets/MenuIcons/dashboard.svg', route: ['/dashboard/retention'] },
      // No listing page exists; only KPI cards and chargeback labels reference cancel requests.
      { id: 'retention-cancel-requests', label: 'Cancel Requests', icon: 'assets/MenuIcons/search.svg', comingSoon: true },
      {
        id: 'retention-offer-builder',
        label: 'Offer Builder',
        icon: 'assets/MenuIcons/offer-builder.svg',
        route: ['/retention'],
        adminOnly: true,
      },
      // Opens the new portal builder; the legacy /customer-portals page stays under the Legacy menu.
      {
        id: 'retention-customer-portal',
        label: 'Customer Portal',
        icon: 'assets/MenuIcons/order-sub-portal.svg',
        route: ['/order-management-portal'],
        requiresAccount: true,
      },
      {
        id: 'retention-reporting',
        label: 'Reporting & Analytics',
        icon: 'assets/MenuIcons/reporting.svg',
        children: [
          { id: 'retention-subscription-report', label: 'Subscription Report', route: ['/subscription-retention-report-v3'] },
          { id: 'retention-order-report', label: 'Order Report', route: ['/order-retention-report-v3'] },
          { id: 'retention-single-step-report', label: 'Single Step Report', route: ['/single-step-report'], adminOnly: true },
        ],
      },
      {
        id: 'retention-settings',
        label: 'Retention Settings',
        icon: 'assets/MenuIcons/settings-sliders.svg',
        route: ['/retention-settings'],
        adminOnly: true,
      },
    ],
  },
  {
    id: MenuId.Returns,
    label: 'Returns',
    icon: 'assets/MenuIcons/returns-product.svg',
    items: [
      { id: 'returns-dashboard', label: 'Dashboard', icon: 'assets/MenuIcons/dashboard.svg', route: ['/dashboard/returns'] },
      // No listing page exists; only KPI cards and chargeback labels reference return requests.
      { id: 'returns-return-requests', label: 'Return Requests', icon: 'assets/MenuIcons/search.svg', comingSoon: true },
      {
        id: 'returns-builder',
        label: 'Return Builder',
        icon: 'assets/MenuIcons/offer-builder.svg',
        route: ['/returns'],
        requiresAccount: true,
        adminOnly: true,
      },
      { id: 'returns-portal', label: 'Returns Portal', icon: 'assets/MenuIcons/order-sub-portal.svg', comingSoon: true },
      {
        id: 'returns-reporting',
        label: 'Reporting & Analytics',
        icon: 'assets/MenuIcons/reporting.svg',
        children: [
          { id: 'returns-report', label: 'Return Report', route: ['/return-retention-report-v3'], requiresAccount: true, adminOnly: true },
        ],
      },
      {
        id: 'returns-settings',
        label: 'Returns Settings',
        icon: 'assets/MenuIcons/settings-sliders.svg',
        route: ['/returns-settings'],
        requiresAccount: true,
        adminOnly: true,
      },
    ],
  },
  {
    id: MenuId.TrustIq,
    label: 'TrustIQ',
    icon: 'assets/MenuIcons/trustiq-product.svg',
    items: [
      {
        id: 'trustiq-dashboard',
        label: 'Dashboard',
        icon: 'assets/MenuIcons/dashboard.svg',
        route: ['/gateway-dashboard'],
        requiresAccount: true,
        adminOnly: true,
      },
      {
        id: 'trustiq-search-transactions',
        label: 'Search Transactions',
        icon: 'assets/MenuIcons/search.svg',
        route: ['/checkouts', 'transaction-summary'],
        requiresAccount: true,
        adminOnly: true,
      },
      {
        id: NavItemId.TrustIqMerchantProfiles,
        label: 'Merchant Profiles',
        icon: 'assets/MenuIcons/merchant-profiles.svg',
        route: ['/merchant-profiles'],
        queryParams: { gateway: true },
        requiresAccount: true,
        adminOnly: true,
      },
      {
        id: 'trustiq-gateways',
        label: 'Gateways',
        icon: 'assets/MenuIcons/gateways.svg',
        route: ['/checkouts', 'gateway-profiles'],
        requiresAccount: true,
        adminOnly: true,
      },
      {
        id: 'trustiq-reporting',
        label: 'Reporting & Analytics',
        icon: 'assets/MenuIcons/reporting.svg',
        children: [
          {
            id: 'trustiq-performance-report',
            label: 'Gateway Performance',
            route: ['/gateway-performance-report-v3'],
            requiresAccount: true,
            adminOnly: true,
          },
        ],
      },
    ],
  },
  {
    id: MenuId.Resolve,
    label: 'Resolve',
    icon: 'assets/MenuIcons/resolve-product.svg',
    items: [
      {
        id: 'resolve-dashboard',
        label: 'Dashboard',
        icon: 'assets/MenuIcons/dashboard.svg',
        route: ['/dashboard/resolve'],
        adminOnly: true,
        adminOnlyBadge: true,
      },
      { id: 'resolve-search-transactions', label: 'Search Transactions', icon: 'assets/MenuIcons/search.svg', comingSoon: true },
      {
        id: NavItemId.ResolveMerchantProfiles,
        label: 'Merchant Profiles',
        icon: 'assets/MenuIcons/merchant-profiles.svg',
        route: ['/merchant-profiles'],
        queryParams: { resolve: true },
      },
      {
        id: 'resolve-paths',
        label: 'Refund Offer Builder',
        icon: 'assets/MenuIcons/offer-builder.svg',
        route: ['/resolve-paths'],
        adminOnly: true,
      },
      {
        id: 'resolve-settings',
        label: 'Resolve Settings',
        icon: 'assets/MenuIcons/settings-sliders.svg',
        route: ['/resolve-settings'],
        requiresAccount: true,
      },
      {
        id: 'resolve-reporting',
        label: 'Reporting & Analytics',
        icon: 'assets/MenuIcons/reporting.svg',
        children: [
          { id: 'resolve-chargeback-reports', label: 'Chargeback Reports', route: ['/chargeback-reports'], requiresAccount: true },
          { id: 'resolve-sp-touched-reports', label: 'SP Touched Reports', route: ['/sp-touched-reports'], requiresAccount: true },
          { id: 'resolve-refund-reports', label: 'Refund Reports', route: ['/refund-reports'], requiresAccount: true },
          {
            id: 'resolve-report',
            label: 'Resolve Report',
            route: ['/resolve-report'],
            requiresAccount: true,
            adminOnly: true,
            adminOnlyBadge: true,
          },
        ],
      },
    ],
  },
  {
    id: MenuId.Support,
    label: 'Customer Support',
    icon: 'assets/NavLogos/smart-desk.svg',
    items: [
      { id: 'support-portal', label: 'Support Portal', icon: 'assets/MenuIcons/portals.svg', comingSoon: true },
    ],
  },
  { id: MenuId.Engage, label: 'Engage', icon: 'assets/NavLogos/product.svg', items: [] },
];

export const ProductsSectionLabel = 'SOLVPATH PRODUCTS';
